import { useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { usePipelineContext } from '../../context';

interface TrashDropZoneProps {
  variant: 'compact' | 'full';
}

/**
 * Trash Drop Zone - Drag cards here to remove from pipeline
 * Supports two variants: compact (above pipeline) and full (below pipeline)
 */
export function TrashDropZone({ variant }: TrashDropZoneProps) {
  const { toast } = useToast();
  const { state, actions } = usePipelineContext();
  const draggedCustomer = state.draggedCustomer;

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.add(
      'bg-red-50',
      'dark:bg-red-950/30',
      'border-red-400',
      'dark:border-red-500',
      'shadow-xl',
      variant === 'compact' ? 'scale-[1.02]' : 'scale-105'
    );
  }, [variant]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.transform = '';
    e.currentTarget.style.transition = '';
    e.currentTarget.classList.remove(
      'bg-red-50',
      'dark:bg-red-950/30',
      'border-red-400',
      'dark:border-red-500',
      'shadow-xl',
      variant === 'compact' ? 'scale-[1.02]' : 'scale-105'
    );
  }, [variant]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.remove(
        'bg-red-50',
        'dark:bg-red-950/30',
        'border-red-400',
        'dark:border-red-500',
        'shadow-xl',
        variant === 'compact' ? 'scale-[1.02]' : 'scale-105'
      );

      if (draggedCustomer) {
        actions.onTrashDrop();
        toast({
          title: 'Lead Removed',
          description: `${draggedCustomer.name} removed from pipeline`,
        });
      }
    },
    [draggedCustomer, actions, toast, variant]
  );

  if (variant === 'compact') {
    return (
      <div className="mb-6">
        <div
          className="group border-2 border-dashed border-slate-300/60 dark:border-slate-600/60 rounded-2xl p-6 flex items-center justify-center bg-slate-50/50 dark:bg-slate-800/50 backdrop-blur-sm transition-all duration-300 hover:border-red-300/80 dark:hover:border-red-500/60 hover:shadow-lg"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          title="Drag leads here to remove from pipeline"
        >
          <Trash2 className="h-5 w-5 text-slate-400 dark:text-slate-500 group-hover:text-red-500 transition-colors mr-2" />
          <span className="text-sm text-slate-500 dark:text-slate-400 group-hover:text-red-500 font-medium">
            Drag here to remove from pipeline
          </span>
        </div>
      </div>
    );
  }

  // Full variant (below pipeline)
  return (
    <div className="mt-10 mb-8">
      <div
        className="group border-2 border-dashed border-slate-300/60 dark:border-slate-600/60 rounded-3xl p-8 flex flex-row items-center justify-center bg-slate-50/30 dark:bg-slate-800/20 transition-all duration-300 hover:border-red-300/80 dark:hover:border-red-500/60 hover:shadow-lg"
        style={{ minHeight: '120px' }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="w-16 h-16 bg-slate-100/80 dark:bg-slate-700/80 rounded-2xl flex items-center justify-center mr-6 group-hover:bg-red-100 dark:group-hover:bg-red-900/30 transition-all duration-300">
          <Trash2 className="h-7 w-7 text-slate-400 dark:text-slate-500 group-hover:text-red-500 transition-colors duration-300" />
        </div>
        <div className="text-center">
          <p className="text-slate-700 dark:text-slate-200 font-semibold text-lg mb-2">
            Remove from Pipeline
          </p>
          <p className="text-slate-500 dark:text-slate-400">
            Drag leads here to remove from pipeline (they'll remain in your customer list)
          </p>
        </div>
      </div>
    </div>
  );
}
